import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Save, Send, CheckCircle, Loader2, FileText } from 'lucide-react';
import { FIELD_SECTIONS } from '../applications/loanFieldConstants';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BorrowerApplicationView = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [application, setApplication] = useState(null);
  const [formData, setFormData] = useState({});
  const [productType, setProductType] = useState('');

  useEffect(() => {
    const fetchApp = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/shared/borrower/${token}`);
        const app = res.data.application;
        setApplication(app);
        setProductType(app.product_type || '');

        const dataKeyMap = {
          sba_loan: 'sba_loan_data', term_loan: 'term_loan_data',
          term_loan_10yr: 'term_loan_10yr_data', term_loan_3_5yr: 'term_loan_3_5yr_data',
          term_loc_hybrid: 'term_loc_hybrid_data', line_of_credit: 'line_of_credit_data',
          equipment_financing: 'equipment_financing_data', growth_term_loan: 'term_loan_3_5yr_data',
        };
        const key = dataKeyMap[app.product_type] || (app.product_type ? `${app.product_type}_data` : '');
        setFormData(app[key] || {});

        if (app.status === 'submitted') setSubmitted(true);
      } catch (err) {
        setError('This link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchApp();
  }, [token]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${BACKEND_URL}/api/shared/borrower/${token}`, {
        loan_data: formData,
        submit: false,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!window.confirm('Are you sure you want to submit? You won\'t be able to make changes after submitting.')) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.put(`${BACKEND_URL}/api/shared/borrower/${token}`, {
        loan_data: formData,
        submit: true,
      });
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-slate-400 mx-auto mb-3" />
          <p className="text-slate-500">Loading application...</p>
        </div>
      </div>
    );
  }

  if (error && !application) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-slate-800 mb-2">Link Unavailable</h2>
            <p className="text-sm text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="h-14 w-14 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Application Submitted</h2>
            <p className="text-sm text-slate-500">Thank you! Your application has been submitted for review. You will be contacted with next steps.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sections = FIELD_SECTIONS[productType] || [];
  const productLabel = (productType || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#081120] text-white py-5 px-6 shadow-lg">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Quidity Direct</h1>
            <p className="text-slate-300 text-sm mt-0.5">Complete Your {productLabel} Application</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 gap-1.5"
              data-testid="borrower-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Progress'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto py-8 px-4">
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        {saved && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <AlertDescription className="text-green-700 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" /> Progress saved! You can close this page and return later using the same link.
            </AlertDescription>
          </Alert>
        )}

        {/* Form sections */}
        {sections.map((section, idx) => (
          <Card key={idx} className="mb-6" data-testid={`section-${idx}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-slate-800">{section.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {section.fields.map((field) => (
                  <div key={field.key} className={field.key.includes('address') || field.fmt === 'textarea' ? 'sm:col-span-2' : ''}>
                    <Label className="text-xs text-slate-600 mb-1 block">{field.label}</Label>
                    {field.fmt === 'bool' ? (
                      <select
                        value={formData[field.key] === true ? 'true' : formData[field.key] === false ? 'false' : ''}
                        onChange={(e) => handleChange(field.key, e.target.value === 'true')}
                        className="w-full border rounded-md px-3 py-2 text-sm"
                        data-testid={`field-${field.key}`}
                      >
                        <option value="">Select...</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    ) : field.fmt === 'select' ? (
                      <select
                        value={formData[field.key] ?? ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className="w-full border rounded-md px-3 py-2 text-sm"
                        data-testid={`field-${field.key}`}
                      >
                        <option value="">Select...</option>
                        {(field.options || []).map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : field.fmt === 'textarea' ? (
                      <textarea
                        rows={4}
                        value={formData[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        placeholder={field.label}
                        className="w-full border rounded-md px-3 py-2 text-sm"
                        data-testid={`field-${field.key}`}
                      />
                    ) : (
                      <Input
                        type={field.fmt === 'currency' ? 'number' : 'text'}
                        value={formData[field.key] || ''}
                        onChange={(e) => handleChange(field.key, field.fmt === 'currency' ? parseFloat(e.target.value) || '' : e.target.value)}
                        placeholder={field.label}
                        data-testid={`field-${field.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Action buttons */}
        <div className="flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4 border-t border-slate-200">
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="outline"
            className="gap-1.5"
            data-testid="borrower-save-bottom-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Progress
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#0E333F] hover:bg-[#081120] gap-1.5"
            data-testid="borrower-submit-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Submit Application
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BorrowerApplicationView;
